import type { ReactNode } from "react";
import { ErrorState, LoadingState } from "./AsyncStates";

export interface QueryStatesProps {
  /** True while any dependency this view needs (fleet context, the query
   * itself) is still resolving — renders the loading branch. */
  isLoading: boolean;
  loadingLabel?: string;
  isError: boolean;
  error?: unknown;
  onRetry?: () => void;
  errorTitle?: string;
  /** Defaults to `error.message` when `error` is an Error, else a generic
   * "check your connection" message. */
  errorDescription?: string;
  /**
   * Escape hatch for a page whose error branch isn't a single shape — e.g.
   * SecretsPage treats an `ApiError` with `code === "not_implemented"` as an
   * EmptyState, not a failure. Return a node to render instead of the default
   * ErrorState, or `undefined` to fall through to it.
   */
  renderError?: (error: unknown) => ReactNode | undefined;
  isEmpty: boolean;
  empty: ReactNode;
  children: ReactNode;
}

function defaultErrorDescription(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  return "Couldn't load this. Check your connection and try again.";
}

/**
 * Collapses the loading/error/empty/data four-way branch every list page
 * repeats (review FE-L: "table + four-state boilerplate duplicated across
 * pages") into one component. Order matters: loading beats error beats empty
 * beats data, matching every page's existing precedence.
 */
export function QueryStates({
  isLoading,
  loadingLabel,
  isError,
  error,
  onRetry,
  errorTitle,
  errorDescription,
  renderError,
  isEmpty,
  empty,
  children,
}: QueryStatesProps) {
  if (isLoading) return <LoadingState label={loadingLabel} />;

  if (isError) {
    const override = renderError?.(error);
    if (override !== undefined) return <>{override}</>;
    return (
      <ErrorState
        title={errorTitle}
        description={errorDescription ?? defaultErrorDescription(error)}
        onRetry={onRetry}
      />
    );
  }

  if (isEmpty) return <>{empty}</>;

  return <>{children}</>;
}
